import {
  Badge,
  Button,
  Col,
  Flex,
  Icon,
  IconButton,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
  Text,
  Title,
  TyreInfoBox,
} from '@wyz/ui';
import { getTyreIdentifiers, serialize } from '@wyz/utils';
import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router';
import { PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { IUserCustomer } from '~/common/types';
import { useManageParameters } from '~/common/api';
import { useManageSearch } from '~/purchase/api';
import {
  QuoteActions,
  QuoteProximityStocksTable,
  QuoteStocksTable,
} from '~/purchase/components';
import {
  QUOTE_AVAILABILITY_TRANSLATIONS,
  QUOTE_MIN_QUANTITY,
  QuoteHelpers,
} from '~/purchase/helpers';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import {
  IBaseProductSearchQs,
  IQuote,
  IQuoteTyreOffer,
} from '~/purchase/types';

interface ExpandedRowWithTyresProps {
  quote: IQuote;
  selectedCustomer: IUserCustomer;
}

export const ExpandedRowWithTyres = ({
  quote,
  selectedCustomer,
}: ExpandedRowWithTyresProps) => {
  const { t } = useI18n();
  const { cdnUrl } = useConfig();
  const locale = useLocale();
  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });
  const search = useManageSearch<IQuoteTyreOffer>({
    context: {
      customerId: selectedCustomer.id,
      searchType: SEARCH_TYPE_ENUM.QUOTE,
    },
    listByEans: {
      args: {
        eans: quote.quoteLines.flatMap((quoteLine) =>
          quoteLine.quoteTyres
            .map((quoteTyre) => quoteTyre.ean)
            .filter((ean) => ean),
        ),
        quantity: QUOTE_MIN_QUANTITY,
      },
    },
  });

  const [showStockInfos, setShowStockInfos] = useState<Record<string, boolean>>(
    {},
  );

  const [showAllProximityStocks, setShowAllProximityStocks] = useState<
    Record<string, boolean>
  >({});

  const handleShowAllProximityStocks = (ean: string) => {
    setShowAllProximityStocks((prev) => ({ ...prev, [ean]: true }));
  };

  const handleBackToNormalView = (ean: string) => {
    setShowAllProximityStocks((prev) => ({ ...prev, [ean]: false }));
  };

  return (
    <Row className='m-0 mb-5'>
      <Col lg={8}>
        <Text weight='bold' color='secondary-gray'>
          <Trans i18nKey='app.quote.history.table.sub_row.products' />
        </Text>
        <div className='bg-white h-100'>
          <table cellSpacing={0} cellPadding={0} className='nested w-100 mt-3'>
            <thead>
              <tr>
                <th className='text-left' style={{ width: '30%' }}>
                  <Trans i18nKey='app.quote.history.table.sub_row.product' />
                </th>
                <th className='text-center'>
                  <Trans i18nKey='app.common.quantity' />
                </th>
                <th className='text-center'>
                  <Trans i18nKey='app.quote.history.table.sub_row.sub_total' />
                </th>
                <th className='text-center'>
                  <Trans i18nKey='app.common.status.availibity' />
                </th>
                <th className='text-center'>
                  <Trans i18nKey='app.quote.history.table.sub_row.action' />
                </th>
              </tr>
            </thead>
            <tbody>
              {quote.quoteLines.map((line, lineIndex) => {
                return (
                  <>
                    {line.quoteTyres.map((tyre) => {
                      const tyreOffer = search.listByEans.data?.data.find(
                        (tyreOffer) => tyreOffer.tyre.ean === tyre.ean,
                      );
                      const quoteItem =
                        tyreOffer === undefined
                          ? undefined
                          : QuoteHelpers.getItemInfoFromTyreOffer(tyreOffer);
                      const isInStock = quoteItem?.availability === 'IN_STOCK';

                      return (
                        <tr key={tyre.ean}>
                          <td className='p-2'>
                            <TyreInfoBox
                              t={t}
                              tyre={{
                                brand: tyre.brand,
                                dimension: tyre.dimension,
                                homologations: tyre.homologations,
                                prodInfo: tyre.prodInfo,
                                ean: tyre.ean,
                                profile: tyre.profile ?? '',
                                ...getTyreIdentifiers(
                                  {
                                    constructorReference:
                                      tyre.manufacturerReference,
                                    alternativeReference:
                                      tyre.alternativeReference,
                                    cai: tyre.cai,
                                  },
                                  selectedCustomer.settingTemplate
                                    ?.generalSetting.tyreIdentifierFields ??
                                    undefined,
                                ),
                              }}
                              mode='FULL'
                              imageBaseUrl={cdnUrl}
                              direction='horizontal'
                              disabled={!tyre.valid}
                            />
                          </td>
                          <td align='center'>
                            <Text
                              id={`quote-tyre-quantity-${tyre.ean}`}
                              weight='semi-bold'
                            >
                              {tyre.quantity}
                            </Text>
                          </td>
                          <td align='center'>
                            <Text
                              id={`quote-tyre-total-price-${tyre.ean}`}
                              weight='semi-bold'
                            >
                              {locale.formatters.money(tyre.totalPrice, {
                                currency: quote.currency,
                              })}
                            </Text>
                          </td>
                          <td align='center'>
                            {search.listByEans.isLoading ? (
                              <Spinner />
                            ) : (
                              <Flex alignItems='center' className='gap-2 px-3'>
                                <Badge
                                  bg={isInStock ? 'success' : 'info'}
                                  className='flex-1'
                                  pill
                                >
                                  <Trans
                                    i18nKey={
                                      QUOTE_AVAILABILITY_TRANSLATIONS[
                                        quoteItem?.availability ?? 'UNAVAILABLE'
                                      ]
                                    }
                                  />
                                </Badge>
                                <OverlayTrigger
                                  show={Boolean(showStockInfos[tyre.ean])}
                                  placement='top'
                                  overlay={
                                    <Popover
                                      className='py-1 px-2'
                                      style={{ minWidth: 500 }}
                                    >
                                      <div>
                                        <Flex
                                          className='mb-3'
                                          justifyContent='between'
                                          alignItems='center'
                                        >
                                          {showAllProximityStocks[tyre.ean] ? (
                                            <Button
                                              id='back-to-normal-view'
                                              variant='link'
                                              onClick={() =>
                                                handleBackToNormalView(tyre.ean)
                                              }
                                              iconAlign='left'
                                              icon='arrow-left'
                                            >
                                              <Trans i18nKey='app.purchase.quote_search_page.product.stock.back' />
                                            </Button>
                                          ) : (
                                            <Title weight='semi-bold'>
                                              <Trans i18nKey='app.purchase.quote_search_page.product.details.information' />
                                            </Title>
                                          )}
                                          <IconButton
                                            id='close-stock-infos'
                                            variant='tertiary-grey'
                                            icon='close'
                                            iconAlign='right'
                                            onClick={() =>
                                              setShowStockInfos((prev) => ({
                                                ...prev,
                                                [tyre.ean]: false,
                                              }))
                                            }
                                          >
                                            <Trans i18nKey='app.common.button.close' />
                                          </IconButton>
                                        </Flex>
                                        <QuoteStocksTable
                                          stocks={quoteItem?.stocks ?? []}
                                          currency={quote.currency}
                                          ean={tyre.ean}
                                        />

                                        {selectedCustomer.hasProximityStocks && (
                                          <QuoteProximityStocksTable
                                            proximityStocks={
                                              quoteItem?.proximityStocks ?? []
                                            }
                                            onShowAll={() =>
                                              handleShowAllProximityStocks(
                                                tyre.ean,
                                              )
                                            }
                                            showAll={
                                              showAllProximityStocks[tyre.ean]
                                            }
                                            ean={tyre.ean}
                                          />
                                        )}
                                      </div>
                                    </Popover>
                                  }
                                >
                                  <Icon
                                    size='sm'
                                    icon='info-circle'
                                    className={clsx({
                                      'text-success-text': isInStock,
                                      'text-royal-blue': !isInStock,
                                    })}
                                    onClick={() =>
                                      setShowStockInfos((prev) => ({
                                        ...prev,
                                        [tyre.ean]: !prev[tyre.ean],
                                      }))
                                    }
                                  />
                                </OverlayTrigger>
                              </Flex>
                            )}
                          </td>

                          <td align='center'>
                            <PrivateComponent roles={[ROLES.ROLE_LIST_OFFERS]}>
                              {!manageParameters.getSeasons.isSettled && (
                                <Spinner />
                              )}
                              {manageParameters.getSeasons.isSuccess && (
                                <Button
                                  id={`order-${tyre.ean}`}
                                  variant='primary'
                                  disabled={
                                    quoteItem?.availability ===
                                      'NOT_ORDERABLE' ||
                                    quoteItem?.availability === 'UNAVAILABLE' ||
                                    !quoteItem?.tyre.ean
                                  }
                                  size='xxs'
                                  as={Link}
                                  to={`/purchase/result?${serialize({
                                    identifier: tyre.ean,
                                    seasons:
                                      manageParameters.getSeasons.data.data
                                        .filter(
                                          (season) =>
                                            season.name.toUpperCase() ===
                                            tyre.season.toUpperCase(),
                                        )
                                        .map((season) => season.publicKey),
                                    fieldNameSearch: 'EAN',
                                    quantity: tyre.quantity,
                                  } satisfies IBaseProductSearchQs)}`}
                                >
                                  <Trans i18nKey='app.quote.history.table.sub_row.order' />
                                </Button>
                              )}
                            </PrivateComponent>
                          </td>
                        </tr>
                      );
                    })}
                    {lineIndex < quote.quoteLines.length - 1 && (
                      <tr>
                        <td colSpan={7}>
                          <hr
                            className='primary'
                            style={{
                              width: '100%',
                              color: 'var(--primary-color)',
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </Col>
      <Col lg={4}>
        <Text weight='bold' color='secondary-gray'>
          <Trans i18nKey='app.quote.history.table.sub_row.services' />
        </Text>
        <div className='bg-white h-100'>
          <table cellSpacing={0} cellPadding={0} className='nested w-100 mt-3'>
            <thead>
              <tr>
                <th>
                  <Trans i18nKey='app.common.service.quote.code' />
                </th>
                <th>
                  <Trans i18nKey='app.common.description' />
                </th>
                <th className='text-right'>
                  <Trans i18nKey='app.quote.history.table.sub_row.service_price' />
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from(
                new Set(
                  quote.quoteLines
                    .flatMap((line) => line.quoteServices)
                    .map((service) => service.code),
                ),
              ).map((code) => {
                const service = quote.quoteLines
                  .flatMap((line) => line.quoteServices)
                  .find((service) => service.code === code);

                return (
                  <tr key={code}>
                    <td className='p-2'>
                      <Text
                        id={`quote-service-code-${service?.code}`}
                        weight='bold'
                        color='tertiary'
                      >
                        {service?.code}
                      </Text>
                    </td>
                    <td className='p-2'>
                      <Text
                        id={`quote-service-name-${service?.code}`}
                        weight='semi-bold'
                      >
                        {service?.type === 'MOUNTING' ? (
                          <Trans i18nKey='app.common.quote.mounting' />
                        ) : (
                          service?.name
                        )}
                      </Text>
                    </td>
                    <td className='p-2' align='right'>
                      <Text
                        id={`quote-service-price-${service?.code}`}
                        weight='semi-bold'
                      >
                        {locale.formatters.money(service?.totalPrice, {
                          currency: quote.currency,
                        })}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <QuoteActions quote={quote} />
          <div className='mt-3 p-2'>
            <Text weight='bold' color='info'>
              <Trans i18nKey='app.quote.history.table.sub_row.offer.total_price.label' />
              {': '}
            </Text>
            {quote.quoteLines.map((line, index) => (
              <div key={index}>
                <Flex>
                  <Text weight='bold' color='info'>
                    <Trans
                      i18nKey='app.quote.history.table.sub_row.offer.total_price'
                      values={{ index: index + 1 }}
                    />
                    {': '}
                  </Text>
                  <Text
                    weight='bold'
                    color='info'
                    className='px-2'
                    id={`quote-line-total-included-tax-${index}`}
                  >
                    {locale.formatters.money(line.totalIncludedTax, {
                      currency: quote.currency,
                    })}
                  </Text>
                </Flex>
              </div>
            ))}
          </div>
        </div>
      </Col>
    </Row>
  );
};
